import React, { useState, useRef, useEffect } from 'react';
import { useI18n } from '../i18n/I18nProvider.jsx';

// SVG флаги
const FLAGS = {
  en: (
    <svg width="20" height="14" viewBox="0 0 60 40" style={{ borderRadius: 2, flexShrink: 0 }}>
      <rect width="60" height="40" fill="#012169"/>
      <path d="M0 0l60 40M60 0L0 40" stroke="#fff" strokeWidth="6"/>
      <path d="M0 0l60 40M60 0L0 40" stroke="#C8102E" strokeWidth="2"/>
      <rect x="24" y="0" width="12" height="40" fill="#fff"/>
      <rect x="0" y="16" width="60" height="8" fill="#fff"/>
      <rect x="27" y="0" width="6" height="40" fill="#C8102E"/>
      <rect x="0" y="19" width="60" height="2" fill="#C8102E"/>
    </svg>
  ),
  pl: (
    <svg width="20" height="14" viewBox="0 0 60 40" style={{ borderRadius: 2, flexShrink: 0 }}>
      <rect y="0" width="60" height="20" fill="#fff"/>
      <rect y="20" width="60" height="20" fill="#DC143C"/>
    </svg>
  ),
  ru: (
    <svg width="20" height="14" viewBox="0 0 60 40" style={{ borderRadius: 2, flexShrink: 0 }}>
      <rect y="0" width="60" height="13.33" fill="#fff"/>
      <rect y="13.33" width="60" height="13.33" fill="#0039A6"/>
      <rect y="26.66" width="60" height="13.34" fill="#D52B1E"/>
    </svg>
  ),
  de: (
    <svg width="20" height="14" viewBox="0 0 60 40" style={{ borderRadius: 2, flexShrink: 0 }}>
      <rect y="0" width="60" height="13.33" fill="#000"/>
      <rect y="13.33" width="60" height="13.33" fill="#DD0000"/>
      <rect y="26.66" width="60" height="13.34" fill="#FFCE00"/>
    </svg>
  ),
  fr: (
    <svg width="20" height="14" viewBox="0 0 60 40" style={{ borderRadius: 2, flexShrink: 0 }}>
      <rect width="20" height="40" fill="#002395"/>
      <rect x="20" width="20" height="40" fill="#fff"/>
      <rect x="40" width="20" height="40" fill="#ED2939"/>
    </svg>
  ),
  es: (
    <svg width="20" height="14" viewBox="0 0 60 40" style={{ borderRadius: 2, flexShrink: 0 }}>
      <rect y="0" width="60" height="10" fill="#AA151B"/>
      <rect y="10" width="60" height="20" fill="#F1BF00"/>
      <rect y="30" width="60" height="10" fill="#AA151B"/>
    </svg>
  ),
  ua: (
    <svg width="20" height="14" viewBox="0 0 60 40" style={{ borderRadius: 2, flexShrink: 0 }}>
      <rect y="0" width="60" height="20" fill="#0057B7"/>
      <rect y="20" width="60" height="20" fill="#FFD700"/>
    </svg>
  ),
};

export default function LanguageSwitcher() {
  const { lang, setLang, languages } = useI18n();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const current = languages.find(l => l.code === lang) || languages[0];

  useEffect(() => {
    function handleClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  return (
    <>
      <div className="lang-switcher" ref={ref}>
        <button 
          className={`lang-current ${open ? 'active' : ''}`}
          onClick={() => setOpen(o => !o)}
        >
          {FLAGS[current.code]}
          <span className="lang-name">{current.name}</span>
          <span className={`lang-chevron ${open ? 'open' : ''}`}>▾</span>
        </button>

        {open && (
          <div className="lang-dropdown">
            {languages.map(l => (
              <button
                key={l.code}
                className={`lang-option ${l.code === lang ? 'active' : ''}`}
                onClick={() => { setLang(l.code); setOpen(false); }}
              >
                {FLAGS[l.code]}
                <span className="lang-name">{l.name}</span>
                {l.code === lang && (
                  <span className="lang-check">✓</span>
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      <style>{`
        .lang-switcher {
          position: relative;
          display: inline-block;
        }
        
        .lang-current {
          display: flex !important;
          align-items: center !important;
          gap: 8px !important;
          padding: 6px 10px !important;
          font-family: 'Montserrat', sans-serif !important;
          font-size: 12px !important;
          font-weight: 600 !important;
          background: #fff !important;
          border: 1.5px solid var(--border) !important;
          border-radius: 8px !important;
          cursor: pointer !important;
          color: var(--dark) !important;
          min-width: 120px;
          transition: 0.15s !important;
        }
        
        .lang-current:hover {
          border-color: var(--blue) !important;
        }
        
        .lang-current.active {
          border-color: var(--blue) !important;
          box-shadow: 0 0 0 3px var(--blue-light) !important;
        }
        
        .lang-name {
          flex: 1;
          text-align: left;
        }
        
        .lang-chevron {
          font-size: 10px;
          color: var(--muted);
          transition: transform 0.2s;
          display: inline-block;
        }
        
        .lang-chevron.open {
          transform: rotate(180deg);
        }
        
        .lang-dropdown {
          position: absolute;
          top: calc(100% + 4px);
          right: 0;
          background: #fff;
          border-radius: 10px;
          border: 1.5px solid var(--border);
          box-shadow: 0 8px 30px rgba(0,0,0,0.12);
          z-index: 200;
          min-width: 170px;
          overflow: hidden;
          padding: 4px;
        }
        
        .lang-option {
          display: flex;
          align-items: center;
          gap: 8px;
          width: 100%;
          padding: 7px 10px;
          border: none;
          background: transparent;
          border-radius: 7px;
          cursor: pointer;
          font-family: 'Montserrat', sans-serif;
          font-size: 12px;
          font-weight: 500;
          color: var(--dark);
          transition: 0.15s;
        }
        
        .lang-option:hover {
          background: var(--blue-light);
        }
        
        .lang-option.active {
          background: var(--blue-light);
          color: var(--blue);
          font-weight: 600;
        }
        
        .lang-check {
          margin-left: auto;
          color: var(--blue);
        }
      `}</style>
    </>
  );
}